import { useCallback, useEffect, useRef, useState } from 'react';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import { collectFeatureLayers } from './useExternalLayerTimeExtent';
import type { ArcgisMapView } from './arcgisView';
import { useArcgisSketchLayer } from './useArcgisSketchLayer';
import { arcgisGeometryToGeoJson } from './arcgisGeometryToGeoJson';
import {
  buildClickedFeaturePickPayload,
  resolveHitToPersistedLayer,
  spatialFilterFromGeoJson,
} from './resolveAiMapPick';
import { isAreaGeometry } from 'components/DisasterGPT/utils/aiMapSpatialFilter';
import type { AiDrawnAreaPayload, AiLayerPickPayload } from './aiMapPick.types';

type UseAiMapPickArgs = {
  mapView: ArcgisMapView | null;
  isMapVisible: boolean;
  aiLayerPickMode: boolean;
  /** IAP symbol drawing owns the map — do not steal clicks. */
  symbolDrawingActive: boolean;
  addedExternalLayersRef: React.MutableRefObject<Map<string, __esri.Layer[]>>;
  internalGraphicsLayersRef: React.MutableRefObject<Map<string, GraphicsLayer>>;
  selectedInternalLayerVersionByInternalLayerId: Record<string, { id: string }>;
  getVisibleLayersForAI: () => AiLayerPickPayload[];
  pickAiLayer: (payload: AiLayerPickPayload) => void;
  pickAiDrawnArea: (payload: AiDrawnAreaPayload) => void;
  onDrawCancelled?: () => void;
};

/**
 * While the chat select tool is on: hit-test visible FeatureLayers / internal
 * graphics, or start a polygon sketch for a drawn area.
 */
export function useAiMapPick({
  mapView,
  isMapVisible,
  aiLayerPickMode,
  symbolDrawingActive,
  addedExternalLayersRef,
  internalGraphicsLayersRef,
  selectedInternalLayerVersionByInternalLayerId,
  getVisibleLayersForAI,
  pickAiLayer,
  pickAiDrawnArea,
  onDrawCancelled,
}: UseAiMapPickArgs) {
  const [aiAreaDrawActive, setAiAreaDrawActive] = useState(false);
  const aiAreaDrawActiveRef = useRef(false);
  const pendingAiAreaDrawRef = useRef(false);
  const pickEnabled = aiLayerPickMode && isMapVisible && !symbolDrawingActive;
  const suppressCancelNotifyRef = useRef(false);
  const onDrawCancelledRef = useRef(onDrawCancelled);
  onDrawCancelledRef.current = onDrawCancelled;

  const notifyDrawCancelled = useCallback(() => {
    onDrawCancelledRef.current?.();
  }, []);

  const onCreateComplete = useCallback(
    async ({ geometry }: { geometry: __esri.GeometryUnion }) => {
      const geoJson = await arcgisGeometryToGeoJson(geometry);
      if (!isAreaGeometry(geoJson)) return;
      pickAiDrawnArea({
        spatialFilter: geoJson,
        spatialFilterLabel: 'Area on map',
      });
      aiAreaDrawActiveRef.current = false;
      setAiAreaDrawActive(false);
    },
    [pickAiDrawnArea]
  );

  const { sketchViewModelRef, isSketchReady, clearDrawingLayer, cancelActiveDrawing } =
    useArcgisSketchLayer({
      mapView: pickEnabled ? mapView : null,
      layerTitle: 'AI map select',
      onCreateComplete: ({ geometry }) => {
        void onCreateComplete({ geometry });
      },
      onCreateCancel: () => {
        pendingAiAreaDrawRef.current = false;
        aiAreaDrawActiveRef.current = false;
        setAiAreaDrawActive(false);
        if (!suppressCancelNotifyRef.current) {
          notifyDrawCancelled();
        }
      },
    });

  const cancelAiAreaDraw = useCallback(() => {
    pendingAiAreaDrawRef.current = false;
    const wasCreating = sketchViewModelRef.current?.state === 'active';
    cancelActiveDrawing();
    clearDrawingLayer();
    aiAreaDrawActiveRef.current = false;
    setAiAreaDrawActive(false);
    if (!wasCreating) {
      notifyDrawCancelled();
    }
  }, [
    cancelActiveDrawing,
    clearDrawingLayer,
    notifyDrawCancelled,
    sketchViewModelRef,
  ]);

  const beginAiAreaDraw = useCallback(() => {
    if (!pickEnabled || !sketchViewModelRef.current) return false;
    suppressCancelNotifyRef.current = true;
    cancelActiveDrawing();
    clearDrawingLayer();
    suppressCancelNotifyRef.current = false;
    sketchViewModelRef.current.create('polygon');
    aiAreaDrawActiveRef.current = true;
    setAiAreaDrawActive(true);
    return true;
  }, [cancelActiveDrawing, clearDrawingLayer, pickEnabled, sketchViewModelRef]);

  const startAiAreaDraw = useCallback(() => {
    pendingAiAreaDrawRef.current = true;
    if (beginAiAreaDraw()) {
      pendingAiAreaDrawRef.current = false;
    }
  }, [beginAiAreaDraw]);

  useEffect(() => {
    if (!pendingAiAreaDrawRef.current) return;
    if (!pickEnabled || !isSketchReady || !sketchViewModelRef.current) return;
    pendingAiAreaDrawRef.current = false;
    beginAiAreaDraw();
  }, [beginAiAreaDraw, isSketchReady, mapView, pickEnabled, sketchViewModelRef]);

  useEffect(() => {
    if (pickEnabled) return;
    pendingAiAreaDrawRef.current = false;
    cancelAiAreaDraw();
  }, [cancelAiAreaDraw, pickEnabled]);

  useEffect(() => {
    if (!mapView?.container) return;
    const el = mapView.container as HTMLElement;
    if (pickEnabled) {
      el.style.cursor = aiAreaDrawActive ? 'crosshair' : 'pointer';
    }
    return () => {
      el.style.cursor = '';
    };
  }, [aiAreaDrawActive, mapView, pickEnabled]);

  const versionByInternalRef = useRef(
    selectedInternalLayerVersionByInternalLayerId
  );
  versionByInternalRef.current = selectedInternalLayerVersionByInternalLayerId;

  const getVisibleLayersRef = useRef(getVisibleLayersForAI);
  getVisibleLayersRef.current = getVisibleLayersForAI;

  const pickAiLayerRef = useRef(pickAiLayer);
  pickAiLayerRef.current = pickAiLayer;

  useEffect(() => {
    if (!mapView || !pickEnabled) return;

    const handleClick = async (event: __esri.ViewClickEvent) => {
      if (aiAreaDrawActiveRef.current) return;
      if (sketchViewModelRef.current?.state === 'active') return;

      const include: __esri.Layer[] = [];
      for (const layers of addedExternalLayersRef.current.values()) {
        for (const layer of layers) {
          if (layer.visible) {
            include.push(...collectFeatureLayers([layer]));
          }
        }
      }
      for (const graphicsLayer of internalGraphicsLayersRef.current.values()) {
        if (graphicsLayer.visible) include.push(graphicsLayer);
      }
      if (include.length === 0) return;

      const hit = await mapView.hitTest(event, { include });
      const graphicHit = hit.results.find(
        (result) =>
          result.type === 'graphic' && result.graphic?.geometry != null
      );
      if (!graphicHit || graphicHit.type !== 'graphic') return;

      const resolved = resolveHitToPersistedLayer(
        graphicHit.graphic.layer,
        addedExternalLayersRef.current,
        internalGraphicsLayersRef.current,
        versionByInternalRef.current
      );
      if (!resolved) return;

      event.stopPropagation();

      const meta = getVisibleLayersRef
        .current()
        .find(
          (layer) =>
            layer.kind === resolved.kind && layer.id === resolved.layerId
        );
      if (!meta) return;

      const hitGeometry = graphicHit.graphic.geometry;
      if (!hitGeometry) return;

      let spatialFilter;
      try {
        const geoJson = await arcgisGeometryToGeoJson(hitGeometry);
        spatialFilter = spatialFilterFromGeoJson(geoJson);
      } catch {
        spatialFilter = undefined;
      }

      pickAiLayerRef.current(
        buildClickedFeaturePickPayload({
          meta,
          graphic: graphicHit.graphic,
          spatialFilter,
        })
      );
    };

    const handle = mapView.on('click', handleClick);
    return () => {
      handle.remove();
    };
  }, [
    addedExternalLayersRef,
    internalGraphicsLayersRef,
    mapView,
    pickEnabled,
    sketchViewModelRef,
  ]);

  return {
    startAiAreaDraw,
    cancelAiAreaDraw,
    aiAreaDrawActive,
  };
}
